// 🔥 통합 디바이스 데이터 - 슬림화된 버전
// [배경] 기존 2096줄을 ~100줄로 줄인 효율적인 구조
//        데이터는 별도 파일로 분리하고, 헬퍼 함수로 생성

export type Brand = 'Apple' | 'Samsung';
export type DeviceCategory = '스마트폰' | '태블릿' | '웨어러블';

export interface UnifiedDevice {
  // 고유 식별자
  id: string; // brand_category_series_model_storage 형태의 정규화된 키
  
  // 기본 정보
  brand: Brand;
  category: DeviceCategory;
  series: string; // "iPhone 15", "Galaxy S", "iPad Pro", "AirPods"
  
  // 모델 정보
  modelEn: string; // "iPhone 15 Pro Max"
  modelKo: string; // "아이폰15 PRO MAX"
  
  // 구성 정보
  storage: string; // "256GB", "128GB" 등
  connectivity?: string; // "Wi-Fi", "Cellular", "Lightning", "USB-C" 등
  color?: string; // "스페이스 그레이", "실버" 등
  
  // 메타 정보
  releaseYear?: number;
  modelNumber?: string; // "A2484", "A1722" 등 애플 모델 번호
  recommended?: boolean; // UI에서 추천 표시
  
  // 이미지
  image: string; // 표준화된 이미지 경로
  
  // 검색용
  searchTerms: string[]; // 모든 가능한 검색어 (소문자)
}

// 🔧 헬퍼 함수와 데이터 import
import { 
  createDevice, 
  createIPhoneSeries, 
  createSamsungSeries,
  createIPadSeries,
  createSamsungTabletSeries,
  createAirPods,
  normalizeId
} from './data/deviceDataHelpers';

import { IPHONE_DATA } from './data/iPhoneData';
import { SAMSUNG_PHONE_DATA } from './data/samsungPhoneData';
import { IPAD_DATA } from './data/iPadData';
import { SAMSUNG_TABLET_DATA } from './data/samsungTabletData';
import { AIRPODS_MODELS } from './data/airPodsData';

// 🔗 통합 데이터 생성 (헬퍼 함수 사용)
const APPLE_SMARTPHONES = IPHONE_DATA.flatMap(createIPhoneSeries);
const SAMSUNG_SMARTPHONES = SAMSUNG_PHONE_DATA.flatMap(createSamsungSeries);
const APPLE_TABLETS = IPAD_DATA.flatMap(createIPadSeries);
const SAMSUNG_TABLETS = SAMSUNG_TABLET_DATA.flatMap(createSamsungTabletSeries);
// AirPods는 기존 DeviceModel 구조를 UnifiedDevice로 변환
const APPLE_WEARABLES = AIRPODS_MODELS.flatMap(model =>
  (model.subModels || []).map(subModel => createDevice({
    brand: 'Apple',
    category: '웨어러블', 
    series: model.name.includes('Pro') ? 'AirPods Pro' : 
           model.name.includes('Max') ? 'AirPods Max' : 'AirPods',
    modelEn: model.name,
    modelKo: model.name,
    storage: subModel.connectivity || '기본',
    connectivity: subModel.connectivity,
    color: subModel.color,
    recommended: model.recommended,
    image: model.image,
    modelNumber: subModel.modelNumber
  }))
);

// 🔗 전체 디바이스 데이터 통합
export const UNIFIED_DEVICE_DATA: UnifiedDevice[] = [
  ...APPLE_SMARTPHONES,
  ...SAMSUNG_SMARTPHONES,
  ...APPLE_TABLETS,
  ...SAMSUNG_TABLETS,
  ...APPLE_WEARABLES
];

// 🔍 검색 및 필터링 함수들
export const searchDevices = (query: string): UnifiedDevice[] => {
  if (!query.trim()) return UNIFIED_DEVICE_DATA;
  
  const normalizedQuery = query.toLowerCase();
  return UNIFIED_DEVICE_DATA.filter(device => 
    device.searchTerms.some(term => term.includes(normalizedQuery)) ||
    device.modelKo.toLowerCase().includes(normalizedQuery) ||
    device.modelEn.toLowerCase().includes(normalizedQuery)
  );
};

export const getDevicesByCategory = (category: DeviceCategory): UnifiedDevice[] =>
  UNIFIED_DEVICE_DATA.filter(device => device.category === category);

export const getDevicesByBrand = (brand: Brand): UnifiedDevice[] =>
  UNIFIED_DEVICE_DATA.filter(device => device.brand === brand);

export const getDevicesByCategoryAndBrand = (category: DeviceCategory, brand: Brand): UnifiedDevice[] =>
  UNIFIED_DEVICE_DATA.filter(device => device.category === category && device.brand === brand);

export const getDeviceById = (id: string): UnifiedDevice | undefined =>
  UNIFIED_DEVICE_DATA.find(device => device.id === id);

export const getRecommendedDevices = (): UnifiedDevice[] =>
  UNIFIED_DEVICE_DATA.filter(device => device.recommended);

export const getDevicesByReleaseYear = (year: number): UnifiedDevice[] =>
  UNIFIED_DEVICE_DATA.filter(device => device.releaseYear === year);

// 🎯 가격 시스템 연동용 함수들
export const buildPriceKey = (device: UnifiedDevice): string => device.id;

export const findDeviceByLegacyFormat = (deviceModel: string, storage?: string): UnifiedDevice | undefined => {
  return UNIFIED_DEVICE_DATA.find(device => 
    device.modelKo === deviceModel && 
    (!storage || device.storage === storage)
  );
};

// 📊 통계 및 메타데이터
export const getDeviceStats = () => ({
  total: UNIFIED_DEVICE_DATA.length,
  byCategory: {
    '스마트폰': getDevicesByCategory('스마트폰').length,
    '태블릿': getDevicesByCategory('태블릿').length,
    '웨어러블': getDevicesByCategory('웨어러블').length,
  },
  byBrand: {
    'Apple': getDevicesByBrand('Apple').length,
    'Samsung': getDevicesByBrand('Samsung').length,
  },
  recommended: getRecommendedDevices().length
});

// 🔄 레거시 호환성 (기존 코드들이 일시적으로 사용할 수 있도록)
export const SMARTPHONE_CATALOG = getDevicesByCategory('스마트폰');
export const TABLET_CATALOG = getDevicesByCategory('태블릿');
export const WEARABLE_CATALOG = getDevicesByCategory('웨어러블');
export const ALL_DEVICE_CATALOG = UNIFIED_DEVICE_DATA;

// 유틸리티 함수 export
export { normalizeId };
